import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { imageUrl } from '../lib/images'
import { useCart } from '../context/CartContext'

export default function FestUdlejningPage() {
  const { addRentalToCart, openCart } = useCart()
  // Rental configuration state for the interactive calculator / inquiry form
  const [selectedItems, setSelectedItems] = useState({
    softice: false,
    softiceExtraMix: 0,
    slushice: false,
    slushiceExtraJuice: 0,
    hoppeborg: false,
    rodeoTyr: false,
    delivery: 'pickup', // 'pickup' | 'delivery'
  })

  // Booking form state
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    event_date: '',
    event_address: '',
    message: '',
    website_url: '', // Honeypot
  })

  const [formStatus, setFormStatus] = useState({
    submitting: false,
    success: false,
    error: null,
  })

  // Calculations
  const softiceBase = selectedItems.softice ? 1250 : 0
  const softiceMixCost = selectedItems.softice ? selectedItems.softiceExtraMix * 75 : 0
  const softiceDelivery = selectedItems.softice && selectedItems.delivery === 'delivery' ? 350 : 0

  const slushiceBase = selectedItems.slushice ? 625 : 0
  const slushiceJuiceCost = selectedItems.slushice ? selectedItems.slushiceExtraJuice * 35 : 0
  const slushiceDelivery = selectedItems.slushice && selectedItems.delivery === 'delivery' ? 350 : 0

  const hoppeborgBase = selectedItems.hoppeborg ? 1500 : 0
  const hoppeborgDelivery = selectedItems.hoppeborg && selectedItems.delivery === 'delivery' ? 500 : 0

  const rodeoBase = selectedItems.rodeoTyr ? 3500 : 0
  const rodeoDelivery = selectedItems.rodeoTyr && selectedItems.delivery === 'delivery' ? 750 : 0

  const subtotalEquipment = softiceBase + softiceMixCost + slushiceBase + slushiceJuiceCost + hoppeborgBase + rodeoBase
  const totalDeliveryCost = softiceDelivery + slushiceDelivery + hoppeborgDelivery + rodeoDelivery
  const grandTotal = subtotalEquipment + totalDeliveryCost

  const hasSelection = selectedItems.softice || selectedItems.slushice || selectedItems.hoppeborg || selectedItems.rodeoTyr

  const handleItemToggle = (key) => {
    setSelectedItems((prev) => {
      const next = { ...prev, [key]: !prev[key] }
      if (!next.softice) next.softiceExtraMix = 0
      if (!next.slushice) next.slushiceExtraJuice = 0
      return next
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formData.name.trim() || !formData.phone.trim() || !formData.email.trim()) {
      setFormStatus({
        submitting: false,
        success: false,
        error: 'Udfyld venligst navn, telefonnummer og e-mail.',
      })
      return
    }

    setFormStatus({ submitting: true, success: false, error: null })

    // Build human-readable breakdown of requested items
    const rentalSummaryList = []
    if (selectedItems.softice) {
      rentalSummaryList.push(
        `Softicemaskine (1.250 kr. inkl. mix til 20 pers.)` +
          (selectedItems.softiceExtraMix > 0
            ? ` + ${selectedItems.softiceExtraMix} ekstra mixer (${selectedItems.softiceExtraMix * 75} kr.)`
            : '')
      )
    }
    if (selectedItems.slushice) {
      rentalSummaryList.push(
        `Slushicemaskine (625 kr. inkl. 2L saft)` +
          (selectedItems.slushiceExtraJuice > 0
            ? ` + ${selectedItems.slushiceExtraJuice}L ekstra saft (${selectedItems.slushiceExtraJuice * 35} kr.)`
            : '')
      )
    }
    if (selectedItems.hoppeborg) {
      rentalSummaryList.push('Hoppeborg (1.500 kr. - alt medfølger)')
    }
    if (selectedItems.rodeoTyr) {
      rentalSummaryList.push('Rodeo Tyr (3.500 kr. inkl. styring & madras)')
    }

    const payload = {
      _form: 'fest-og-udlejning',
      name: formData.name.trim(),
      phone: formData.phone.trim(),
      email: formData.email.trim(),
      event_date: formData.event_date || 'Ikke angivet',
      event_address: formData.event_address || 'Ikke angivet',
      levering_valg: selectedItems.delivery === 'delivery' ? 'Ønsker levering og opsætning' : 'Afhenter selv',
      valgte_udlejningsudstyr: rentalSummaryList.length > 0 ? rentalSummaryList.join(' | ') : 'Ingen forudvalgt - generel forespørgsel',
      beregnet_cirkapris: grandTotal > 0 ? `${grandTotal.toLocaleString('da-DK')} kr. inkl. moms` : 'Ikke beregnet',
      message: formData.message.trim() || 'Forespørgsel sendt via fest og udlejningssiden.',
      website_url: formData.website_url, // Honeypot
    }

    try {
      const res = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })

      if (res.ok) {
        setFormStatus({ submitting: false, success: true, error: null })
        setFormData({
          name: '',
          phone: '',
          email: '',
          event_date: '',
          event_address: '',
          message: '',
          website_url: '',
        })
      } else {
        const data = await res.json().catch(() => ({}))
        setFormStatus({
          submitting: false,
          success: false,
          error: data.message || 'Der opstod en fejl ved afsendelse. Prøv venligst igen eller ring direkte.',
        })
      }
    } catch {
      setFormStatus({
        submitting: false,
        success: false,
        error: 'Netværksfejl. Prøv igen eller kontakt os direkte på 25 75 27 24.',
      })
    }
  }

  const handleAddEquipmentToCart = () => {
    if (!hasSelection) {
      const el = document.getElementById('beregner')
      if (el) el.scrollIntoView({ behavior: 'smooth' })
      return
    }

    if (selectedItems.softice) {
      addRentalToCart({
        id: 'softice',
        name: 'Softicemaskine',
        price: 1250,
        quantity: 1,
        image: '/_assets/softicemaskine-professionel-festudlejning.jpg',
        extrasCost: selectedItems.softiceExtraMix * 75,
        optionsNote:
          selectedItems.softiceExtraMix > 0
            ? `Inkl. standard mix + ${selectedItems.softiceExtraMix} ekstra mixer (${selectedItems.softiceExtraMix * 75} kr.)`
            : 'Inkl. mix til 20 personer',
      })
    }
    if (selectedItems.slushice) {
      addRentalToCart({
        id: 'slushice',
        name: 'Slushicemaskine',
        price: 625,
        quantity: 1,
        image: 'https://assets.auxodev.dk/assets/5d78d207-d7b4-4866-8f75-23fa665241c8/image_d0ab64d8.png',
        extrasCost: selectedItems.slushiceExtraJuice * 35,
        optionsNote:
          selectedItems.slushiceExtraJuice > 0
            ? `Inkl. standard saft + ${selectedItems.slushiceExtraJuice} ekstra saft`
            : 'Inkl. standard saft',
      })
    }
    if (selectedItems.hoppeborg) {
      addRentalToCart({
        id: 'hoppeborg',
        name: 'Hoppeborg',
        price: 1500,
        quantity: 1,
        image: 'https://assets.auxodev.dk/assets/5d78d207-d7b4-4866-8f75-23fa665241c8/image_00a53d99.png',
        optionsNote: 'Inkl. blæser og pløkke',
      })
    }
    if (selectedItems.rodeoTyr) {
      addRentalToCart({
        id: 'rodeotyr',
        name: 'Mekanisk Rodeo Tyr',
        price: 3500,
        quantity: 1,
        image: 'https://assets.auxodev.dk/assets/5d78d207-d7b4-4866-8f75-23fa665241c8/image_a87f573e.png',
        optionsNote: 'Professionel rodeotyr med faldpude',
      })
    }
    openCart()
  }

  return (
    <div className="py-10 sm:py-16 space-y-16">
      {/* Hero section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 space-y-6">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#241F1C] tracking-tight leading-tight">
              Fest og udlejning hos Overgaard Pattegrise
            </h1>

            <p className="text-base sm:text-lg text-[#6E635C] leading-relaxed">
              Skal du holde havefest, konfirmation, fødselsdag, byfest eller firmafest? Vi udlejer professionelt
              festudstyr, der skaber masser af smil og glæde. Fra cremet softice og forfriskende slushice til
              hoppeborg og den populære mekaniske rodeo tyr!
            </p>

            {/* Note regarding self-pickup and delivery */}
            <div className="p-4 bg-[#FAF8F5] border-l-4 border-[#C25E27] text-sm text-[#5C534D] space-y-1">
              <p className="font-bold text-[#241F1C] flex items-center gap-2">
                <i className="fa-solid fa-truck-ramp-box text-[#C25E27]" aria-hidden="true" />
                <span>Afhent selv eller få leveret &amp; opsat</span>
              </p>
              <p>
                Priserne gælder, hvis I selv afhenter udstyret hos os i Sønder Omme. Vi tilbyder naturligvis også
                professionel levering, opsætning og instruktion mod et mindre tillæg.
              </p>
            </div>

            <div className="pt-2 flex flex-wrap gap-3 sm:gap-4">
              <a
                href="#udstyr"
                className="inline-flex items-center gap-2 bg-[#C25E27] hover:bg-[#A84E1C] text-white px-5 py-3 sm:px-7 sm:py-3.5 font-bold text-xs sm:text-sm transition-shadow duration-300 hover:shadow-lg cta-hop"
              >
                <i className="fa-solid fa-eye text-xs" aria-hidden="true" />
                <span>Se udstyr &amp; priser</span>
              </a>
              <a
                href="#beregner"
                className="inline-flex items-center gap-2 bg-transparent text-[#241F1C] border border-[#241F1C]/40 px-5 py-3 sm:px-6 sm:py-3.5 font-bold text-xs sm:text-sm transition-colors hover:bg-[#241F1C] hover:text-white"
              >
                <i className="fa-solid fa-calculator text-xs" aria-hidden="true" />
                <span>Prisberegner &amp; booking</span>
              </a>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative overflow-hidden aspect-[4/3] shadow-lg border border-[#E6DFD5]">
              <img
                src={imageUrl('https://assets.auxodev.dk/assets/5d78d207-d7b4-4866-8f75-23fa665241c8/image_00a53d99.png', 'width=900,quality=80,format=auto')}
                alt="Hoppeborg udlejning til fest"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Product Grid Section */}
      <section id="udstyr" className="max-w-7xl mx-auto px-4 sm:px-6 scroll-mt-24">
        <div className="text-center max-w-3xl mx-auto mb-12 space-y-3">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-[#241F1C]">
            Vores fest- og udlejningsudstyr
          </h2>
          <p className="text-sm sm:text-base text-[#6E635C]">
            Alle priser er inkl. moms ved afhentning hos Overgaard Pattegrise på Simmelbrovej 52, 7260 Sønder Omme.
            Alt nødvendigt udstyr medfølger.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Card 1: Softicemaskine */}
          <div className="bg-white border border-[#E6DFD5] flex flex-col justify-between transition-all duration-300 hover:shadow-xl group">
            <div>
              <div className="aspect-[16/10] overflow-hidden bg-[#FAF8F5] relative">
                <img
                  src={imageUrl('/_assets/softicemaskine-professionel-festudlejning.jpg', 'width=800,quality=80,format=auto')}
                  alt="Softicemaskine udlejning"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>

              <div className="p-6 sm:p-8 space-y-4">
                <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-[#E6DFD5] pb-4">
                  <h3 className="text-2xl font-extrabold text-[#241F1C]">Softicemaskine</h3>
                  <div className="text-right">
                    <span className="text-2xl sm:text-3xl font-black text-[#C25E27]">1.250 kr.</span>
                    <span className="text-xs text-[#6E635C] block">inkl. moms</span>
                  </div>
                </div>

                <p className="text-sm text-[#6E635C] leading-relaxed">
                  Forkæl dine gæster med ægte frisklavet, cremet softice! En professionel bordmodel softicemaskine,
                  der fryser lynhurtigt ind og er utrolig nem at betjene.
                </p>

                <div className="space-y-2 pt-2">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-[#241F1C]">Hvad er inkluderet?</h4>
                  <ul className="text-xs sm:text-sm text-[#5C534D] space-y-1.5">
                    <li className="flex items-start gap-2">
                      <i className="fa-solid fa-check text-[#C25E27] mt-0.5 text-xs" aria-hidden="true" />
                      <span><strong>Mixer til 20 personer inkluderet</strong> i grundprisen.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="fa-solid fa-check text-[#C25E27] mt-0.5 text-xs" aria-hidden="true" />
                      <span>Ekstra mixer: <strong>75 kr. pr. mixer</strong> (én mixer rækker til ca. 12 personer).</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="fa-solid fa-truck text-[#C25E27] mt-0.5 text-xs" aria-hidden="true" />
                      <span>Mulighed for levering og opsætning: <strong>350 kr.</strong></span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="p-6 sm:p-8 pt-0">
              <button
                type="button"
                onClick={() => {
                  setSelectedItems((prev) => ({ ...prev, softice: true }))
                  const el = document.getElementById('beregner')
                  if (el) el.scrollIntoView({ behavior: 'smooth' })
                }}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#241F1C] hover:bg-[#38322E] text-white px-4 py-3 font-bold text-xs sm:text-sm transition-colors cursor-pointer"
              >
                <i className="fa-solid fa-sliders text-xs text-[#C25E27]" aria-hidden="true" />
                <span>Vælg afhentning / levering &amp; tilpas</span>
              </button>
            </div>
          </div>

          {/* Card 2: Slushicemaskine */}
          <div className="bg-white border border-[#E6DFD5] flex flex-col justify-between transition-all duration-300 hover:shadow-xl group">
            <div>
              <div className="aspect-[16/10] overflow-hidden bg-[#FAF8F5] relative">
                <img
                  src={imageUrl('https://assets.auxodev.dk/assets/5d78d207-d7b4-4866-8f75-23fa665241c8/image_d0ab64d8.png', 'width=800,quality=80,format=auto')}
                  alt="Slushicemaskine udlejning"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>

              <div className="p-6 sm:p-8 space-y-4">
                <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-[#E6DFD5] pb-4">
                  <h3 className="text-2xl font-extrabold text-[#241F1C]">Slushicemaskine</h3>
                  <div className="text-right">
                    <span className="text-2xl sm:text-3xl font-black text-[#C25E27]">625 kr.</span>
                    <span className="text-xs text-[#6E635C] block">inkl. moms</span>
                  </div>
                </div>

                <p className="text-sm text-[#6E635C] leading-relaxed">
                  Lækker, iskold slushice i flotte farver. Perfekt til både børnefødselsdage, konfirmationer og voksne
                  havefester (hvor der også kan tilsættes spiritus til festlige drinks som frozen daiquiri).
                </p>

                <div className="space-y-2 pt-2">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-[#241F1C]">Hvad er inkluderet?</h4>
                  <ul className="text-xs sm:text-sm text-[#5C534D] space-y-1.5">
                    <li className="flex items-start gap-2">
                      <i className="fa-solid fa-check text-[#C25E27] mt-0.5 text-xs" aria-hidden="true" />
                      <span><strong>2 liter koncentreret saft</strong> inkluderet i grundprisen.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="fa-solid fa-check text-[#C25E27] mt-0.5 text-xs" aria-hidden="true" />
                      <span>Ekstra saft: <strong>35 kr. pr. liter</strong> efter de første 2 liter.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="fa-solid fa-truck text-[#C25E27] mt-0.5 text-xs" aria-hidden="true" />
                      <span>Mulighed for levering og opsætning: <strong>350 kr.</strong></span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="p-6 sm:p-8 pt-0">
              <button
                type="button"
                onClick={() => {
                  setSelectedItems((prev) => ({ ...prev, slushice: true }))
                  const el = document.getElementById('beregner')
                  if (el) el.scrollIntoView({ behavior: 'smooth' })
                }}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#241F1C] hover:bg-[#38322E] text-white px-4 py-3 font-bold text-xs sm:text-sm transition-colors cursor-pointer"
              >
                <i className="fa-solid fa-sliders text-xs text-[#C25E27]" aria-hidden="true" />
                <span>Vælg afhentning / levering &amp; tilpas</span>
              </button>
            </div>
          </div>

          {/* Card 3: Hoppeborg */}
          <div className="bg-white border border-[#E6DFD5] flex flex-col justify-between transition-all duration-300 hover:shadow-xl group">
            <div>
              <div className="aspect-[16/10] overflow-hidden bg-[#FAF8F5] relative">
                <img
                  src="https://assets.auxodev.dk/assets/5d78d207-d7b4-4866-8f75-23fa665241c8/image_00a53d99.png"
                  alt="Hoppeborg udlejning"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>

              <div className="p-6 sm:p-8 space-y-4">
                <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-[#E6DFD5] pb-4">
                  <h3 className="text-2xl font-extrabold text-[#241F1C]">Hoppeborg</h3>
                  <div className="text-right">
                    <span className="text-2xl sm:text-3xl font-black text-[#C25E27]">1.500 kr.</span>
                    <span className="text-xs text-[#6E635C] block">inkl. moms</span>
                  </div>
                </div>

                <p className="text-sm text-[#6E635C] leading-relaxed">
                  Giv børnene timevis af aktiv underholdning og sjov leg i haven. En farverig, robust og
                  sikkerhedsgodkendt hoppeborg, der pustes op på få minutter.
                </p>

                <div className="space-y-2 pt-2">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-[#241F1C]">Hvad er inkluderet?</h4>
                  <ul className="text-xs sm:text-sm text-[#5C534D] space-y-1.5">
                    <li className="flex items-start gap-2">
                      <i className="fa-solid fa-check text-[#C25E27] mt-0.5 text-xs" aria-hidden="true" />
                      <span><strong>Alt medfølger til det hele:</strong> Blæser, underlag, pløkker og ledninger.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="fa-solid fa-check text-[#C25E27] mt-0.5 text-xs" aria-hidden="true" />
                      <span>Hurtig og nem opsætning – klar til hop på under 10 minutter.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="fa-solid fa-truck text-[#C25E27] mt-0.5 text-xs" aria-hidden="true" />
                      <span>Mulighed for levering og opsætning: <strong>500 kr.</strong></span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="p-6 sm:p-8 pt-0">
              <button
                type="button"
                onClick={() => {
                  setSelectedItems((prev) => ({ ...prev, hoppeborg: true }))
                  const el = document.getElementById('beregner')
                  if (el) el.scrollIntoView({ behavior: 'smooth' })
                }}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#241F1C] hover:bg-[#38322E] text-white px-4 py-3 font-bold text-xs sm:text-sm transition-colors cursor-pointer"
              >
                <i className="fa-solid fa-sliders text-xs text-[#C25E27]" aria-hidden="true" />
                <span>Vælg afhentning / levering &amp; tilpas</span>
              </button>
            </div>
          </div>

          {/* Card 4: Rodeo Tyr */}
          <div className="bg-white border border-[#E6DFD5] flex flex-col justify-between transition-all duration-300 hover:shadow-xl group">
            <div>
              <div className="aspect-[16/10] overflow-hidden bg-[#FAF8F5] relative">
                <img
                  src={imageUrl('/_assets/image_a87f573e.png', 'width=800,quality=80,format=auto')}
                  alt="Mekanisk Rodeo tyr udlejning"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>

              <div className="p-6 sm:p-8 space-y-4">
                <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-[#E6DFD5] pb-4">
                  <h3 className="text-2xl font-extrabold text-[#241F1C]">Mekanisk Rodeo Tyr</h3>
                  <div className="text-right">
                    <span className="text-2xl sm:text-3xl font-black text-[#C25E27]">3.500 kr.</span>
                    <span className="text-xs text-[#6E635C] block">inkl. moms</span>
                  </div>
                </div>

                <p className="text-sm text-[#6E635C] leading-relaxed">
                  Den ultimative forlystelse og publikumsmagnet! Udfordr kollegerne, vennerne eller familien til at
                  se, hvem der kan blive siddende længst på tyren.
                </p>

                <div className="space-y-2 pt-2">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-[#241F1C]">Hvad er inkluderet?</h4>
                  <ul className="text-xs sm:text-sm text-[#5C534D] space-y-1.5">
                    <li className="flex items-start gap-2">
                      <i className="fa-solid fa-check text-[#C25E27] mt-0.5 text-xs" aria-hidden="true" />
                      <span>Mekanisk tyr med digital styrepult og forskellige sværhedsgrader.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="fa-solid fa-check text-[#C25E27] mt-0.5 text-xs" aria-hidden="true" />
                      <span>Stor, blød oppustelig sikkerhedsmadras med blæser.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="fa-solid fa-truck text-[#C25E27] mt-0.5 text-xs" aria-hidden="true" />
                      <span>Mulighed for levering og opsætning: <strong>750 kr.</strong></span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="p-6 sm:p-8 pt-0">
              <button
                type="button"
                onClick={() => {
                  setSelectedItems((prev) => ({ ...prev, rodeoTyr: true }))
                  const el = document.getElementById('beregner')
                  if (el) el.scrollIntoView({ behavior: 'smooth' })
                }}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#241F1C] hover:bg-[#38322E] text-white px-4 py-3 font-bold text-xs sm:text-sm transition-colors cursor-pointer"
              >
                <i className="fa-solid fa-sliders text-xs text-[#C25E27]" aria-hidden="true" />
                <span>Vælg afhentning / levering &amp; tilpas</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Calculator & Booking Request Section */}
      <section id="beregner" className="bg-[#FAF8F5] py-16 border-y border-[#E6DFD5] scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-12 space-y-3">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-[#241F1C]">
              Prisberegner &amp; leje-forespørgsel
            </h2>
            <p className="text-sm sm:text-base text-[#6E635C]">
              Vælg det udstyr du ønsker at leje, se din samlede pris inkl. moms, og send en uforpligtende forespørgsel med det samme.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* Left: Options & Selection */}
            <div className="lg:col-span-7 bg-white p-6 sm:p-8 border border-[#E6DFD5] shadow-sm space-y-6">
              <h3 className="text-lg font-bold text-[#241F1C] flex items-center gap-2 border-b border-[#E6DFD5] pb-3">
                <i className="fa-solid fa-list-check text-[#C25E27]" aria-hidden="true" />
                <span>1. Vælg udstyr og mængder</span>
              </h3>

              {/* Item 1: Softice */}
              <div
                className={`p-4 border transition-colors ${
                  selectedItems.softice ? 'border-[#C25E27] bg-[#C25E27]/5' : 'border-[#E6DFD5] bg-white'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedItems.softice}
                      onChange={() => handleItemToggle('softice')}
                      className="mt-1 w-4 h-4 text-[#C25E27] border-gray-300 rounded focus:ring-[#C25E27]"
                    />
                    <div>
                      <span className="font-bold text-[#241F1C] block text-base">Softicemaskine</span>
                      <span className="text-xs text-[#6E635C]">
                        1.250 kr. inkl. moms (inkl. mix til 20 pers.)
                      </span>
                    </div>
                  </label>
                  <span className="font-bold text-[#241F1C] text-sm">
                    {selectedItems.softice ? '1.250 kr.' : '-'}
                  </span>
                </div>

                {selectedItems.softice && (
                  <div className="mt-4 pt-3 border-t border-[#E6DFD5] pl-7 space-y-2">
                    <label className="block text-xs font-medium text-[#5C534D]">
                      Ekstra mixer (75 kr. pr. mixer / ca. 12 personer pr. mixer):
                    </label>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() =>
                          setSelectedItems((prev) => ({
                            ...prev,
                            softiceExtraMix: Math.max(0, prev.softiceExtraMix - 1),
                          }))
                        }
                        className="w-8 h-8 flex items-center justify-center border border-[#E6DFD5] bg-white hover:bg-gray-100 font-bold"
                      >
                        -
                      </button>
                      <span className="font-bold text-sm w-8 text-center">{selectedItems.softiceExtraMix}</span>
                      <button
                        type="button"
                        onClick={() =>
                          setSelectedItems((prev) => ({
                            ...prev,
                            softiceExtraMix: prev.softiceExtraMix + 1,
                          }))
                        }
                        className="w-8 h-8 flex items-center justify-center border border-[#E6DFD5] bg-white hover:bg-gray-100 font-bold"
                      >
                        +
                      </button>
                      <span className="text-xs text-[#6E635C]">
                        {selectedItems.softiceExtraMix > 0
                          ? `+${selectedItems.softiceExtraMix * 75} kr. (nok til ca. ${
                              20 + selectedItems.softiceExtraMix * 12
                            } personer i alt)`
                          : 'Standard til 20 personer'}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Item 2: Slushice */}
              <div
                className={`p-4 border transition-colors ${
                  selectedItems.slushice ? 'border-[#C25E27] bg-[#C25E27]/5' : 'border-[#E6DFD5] bg-white'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedItems.slushice}
                      onChange={() => handleItemToggle('slushice')}
                      className="mt-1 w-4 h-4 text-[#C25E27] border-gray-300 rounded focus:ring-[#C25E27]"
                    />
                    <div>
                      <span className="font-bold text-[#241F1C] block text-base">Slushicemaskine</span>
                      <span className="text-xs text-[#6E635C]">
                        625 kr. inkl. moms (inkl. 2 liter saft)
                      </span>
                    </div>
                  </label>
                  <span className="font-bold text-[#241F1C] text-sm">
                    {selectedItems.slushice ? '625 kr.' : '-'}
                  </span>
                </div>

                {selectedItems.slushice && (
                  <div className="mt-4 pt-3 border-t border-[#E6DFD5] pl-7 space-y-2">
                    <label className="block text-xs font-medium text-[#5C534D]">
                      Ekstra liter saft (35 kr. pr. liter efter de første 2 liter):
                    </label>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() =>
                          setSelectedItems((prev) => ({
                            ...prev,
                            slushiceExtraJuice: Math.max(0, prev.slushiceExtraJuice - 1),
                          }))
                        }
                        className="w-8 h-8 flex items-center justify-center border border-[#E6DFD5] bg-white hover:bg-gray-100 font-bold"
                      >
                        -
                      </button>
                      <span className="font-bold text-sm w-8 text-center">{selectedItems.slushiceExtraJuice}</span>
                      <button
                        type="button"
                        onClick={() =>
                          setSelectedItems((prev) => ({
                            ...prev,
                            slushiceExtraJuice: prev.slushiceExtraJuice + 1,
                          }))
                        }
                        className="w-8 h-8 flex items-center justify-center border border-[#E6DFD5] bg-white hover:bg-gray-100 font-bold"
                      >
                        +
                      </button>
                      <span className="text-xs text-[#6E635C]">
                        {selectedItems.slushiceExtraJuice > 0
                          ? `+${selectedItems.slushiceExtraJuice * 35} kr. (${2 + selectedItems.slushiceExtraJuice} liter saft i alt)`
                          : 'Inkl. 2 liter standardsaft'}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Item 3: Hoppeborg */}
              <div
                className={`p-4 border transition-colors ${
                  selectedItems.hoppeborg ? 'border-[#C25E27] bg-[#C25E27]/5' : 'border-[#E6DFD5] bg-white'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedItems.hoppeborg}
                      onChange={() => handleItemToggle('hoppeborg')}
                      className="mt-1 w-4 h-4 text-[#C25E27] border-gray-300 rounded focus:ring-[#C25E27]"
                    />
                    <div>
                      <span className="font-bold text-[#241F1C] block text-base">Hoppeborg</span>
                      <span className="text-xs text-[#6E635C]">
                        1.500 kr. inkl. moms (alt udstyr medfølger)
                      </span>
                    </div>
                  </label>
                  <span className="font-bold text-[#241F1C] text-sm">
                    {selectedItems.hoppeborg ? '1.500 kr.' : '-'}
                  </span>
                </div>
              </div>

              {/* Item 4: Rodeo Tyr */}
              <div
                className={`p-4 border transition-colors ${
                  selectedItems.rodeoTyr ? 'border-[#C25E27] bg-[#C25E27]/5' : 'border-[#E6DFD5] bg-white'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedItems.rodeoTyr}
                      onChange={() => handleItemToggle('rodeoTyr')}
                      className="mt-1 w-4 h-4 text-[#C25E27] border-gray-300 rounded focus:ring-[#C25E27]"
                    />
                    <div>
                      <span className="font-bold text-[#241F1C] block text-base">Mekanisk Rodeo Tyr</span>
                      <span className="text-xs text-[#6E635C]">
                        3.500 kr. inkl. moms (inkl. styring &amp; sikkerhedsmadras)
                      </span>
                    </div>
                  </label>
                  <span className="font-bold text-[#241F1C] text-sm">
                    {selectedItems.rodeoTyr ? '3.500 kr.' : '-'}
                  </span>
                </div>
              </div>

              {/* Delivery method toggle */}
              <div className="pt-4 border-t border-[#E6DFD5] space-y-3">
                <h4 className="text-xs font-bold uppercase tracking-wider text-[#241F1C]">
                  2. Vælg leveringsform
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label
                    className={`p-3 border flex items-center gap-3 cursor-pointer transition-colors ${
                      selectedItems.delivery === 'pickup'
                        ? 'border-[#C25E27] bg-[#C25E27]/5 font-semibold text-[#241F1C]'
                        : 'border-[#E6DFD5] text-[#5C534D]'
                    }`}
                  >
                    <input
                      type="radio"
                      name="delivery_type"
                      checked={selectedItems.delivery === 'pickup'}
                      onChange={() => setSelectedItems((prev) => ({ ...prev, delivery: 'pickup' }))}
                      className="text-[#C25E27] focus:ring-[#C25E27]"
                    />
                    <div>
                      <span className="block text-sm">Jeg afhenter selv</span>
                      <span className="text-xs text-[#6E635C]">Sønder Omme (0 kr.)</span>
                    </div>
                  </label>

                  <label
                    className={`p-3 border flex items-center gap-3 cursor-pointer transition-colors ${
                      selectedItems.delivery === 'delivery'
                        ? 'border-[#C25E27] bg-[#C25E27]/5 font-semibold text-[#241F1C]'
                        : 'border-[#E6DFD5] text-[#5C534D]'
                    }`}
                  >
                    <input
                      type="radio"
                      name="delivery_type"
                      checked={selectedItems.delivery === 'delivery'}
                      onChange={() => setSelectedItems((prev) => ({ ...prev, delivery: 'delivery' }))}
                      className="text-[#C25E27] focus:ring-[#C25E27]"
                    />
                    <div>
                      <span className="block text-sm">Levering &amp; opsætning</span>
                      <span className="text-xs text-[#6E635C]">
                        {totalDeliveryCost > 0 ? `+${totalDeliveryCost} kr. i alt` : 'Se tillæg pr. del'}
                      </span>
                    </div>
                  </label>
                </div>
              </div>

              {/* Price summary box */}
              <div className="bg-[#FAF8F5] p-5 border border-[#E6DFD5] space-y-2">
                <div className="flex justify-between text-xs sm:text-sm text-[#5C534D]">
                  <span>Udstyr i alt:</span>
                  <span className="font-semibold text-[#241F1C]">{subtotalEquipment.toLocaleString('da-DK')} kr.</span>
                </div>

                {selectedItems.delivery === 'delivery' && (
                  <div className="flex justify-between text-xs sm:text-sm text-[#5C534D]">
                    <span>Levering &amp; opsætning:</span>
                    <span className="font-semibold text-[#241F1C]">+{totalDeliveryCost.toLocaleString('da-DK')} kr.</span>
                  </div>
                )}

                <div className="border-t border-[#E6DFD5] pt-2 flex justify-between items-baseline">
                  <span className="font-bold text-base text-[#241F1C]">Samlet pris (inkl. moms):</span>
                  <span className="text-2xl font-black text-[#C25E27]">{grandTotal.toLocaleString('da-DK')} kr.</span>
                </div>

                {/* Add to Cart from calculator */}
                {subtotalEquipment > 0 && (
                  <div className="pt-3">
                    <button
                      type="button"
                      onClick={handleAddEquipmentToCart}
                      className="w-full bg-[#C25E27] hover:bg-[#A84E1C] text-white py-3 px-4 font-bold text-sm shadow-sm transition-all flex items-center justify-center gap-2 cursor-pointer"
                    >
                      <i className="fa-solid fa-basket-shopping text-sm" aria-hidden="true" />
                      <span>Gem &amp; tilføj udstyr til samlet festkurv</span>
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Right: Booking inquiry form */}
            <div className="lg:col-span-5 bg-white p-6 sm:p-8 border border-[#E6DFD5] shadow-sm space-y-5">
              <div>
                <h3 className="text-lg font-bold text-[#241F1C]">Send leje-forespørgsel</h3>
                <p className="text-xs text-[#6E635C] mt-1">
                  Udfyld formularen, så tjekker vi kalenderen og bekræfter ledighed hurtigt.
                </p>
              </div>

              {formStatus.success ? (
                <div className="p-6 bg-green-50 border border-green-200 text-center space-y-3">
                  <i className="fa-solid fa-circle-check text-green-600 text-3xl" aria-hidden="true" />
                  <h4 className="font-bold text-green-900 text-base">Mange tak for din henvendelse!</h4>
                  <p className="text-xs text-green-800 leading-relaxed">
                    Vi har modtaget din forespørgsel på udlejning og vender hurtigst muligt tilbage med svar og bekræftelse.
                  </p>
                  <button
                    type="button"
                    onClick={() => setFormStatus({ submitting: false, success: false, error: null })}
                    className="inline-block mt-2 text-xs font-bold text-green-800 underline hover:text-green-950"
                  >
                    Send en ny forespørgsel
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Antispam honeypot */}
                  <input
                    type="text"
                    name="website_url"
                    value={formData.website_url}
                    onChange={(e) => setFormData((prev) => ({ ...prev, website_url: e.target.value }))}
                    className="sr-only absolute -left-[9999px]"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                  />

                  {formStatus.error && (
                    <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs">
                      {formStatus.error}
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-bold text-[#241F1C] uppercase tracking-wider mb-1">
                      Dit navn *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="F.eks. Peter Hansen"
                      value={formData.name}
                      onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-[#E6DFD5] text-sm focus:outline-none focus:border-[#C25E27]"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-bold text-[#241F1C] uppercase tracking-wider mb-1">
                        Telefon *
                      </label>
                      <input
                        type="tel"
                        required
                        placeholder="25 75 27 24"
                        value={formData.phone}
                        onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                        className="w-full px-3 py-2 border border-[#E6DFD5] text-sm focus:outline-none focus:border-[#C25E27]"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-[#241F1C] uppercase tracking-wider mb-1">
                        E-mail *
                      </label>
                      <input
                        type="email"
                        required
                        placeholder="din@mail.dk"
                        value={formData.email}
                        onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                        className="w-full px-3 py-2 border border-[#E6DFD5] text-sm focus:outline-none focus:border-[#C25E27]"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-bold text-[#241F1C] uppercase tracking-wider mb-1">
                        Dato for arrangement
                      </label>
                      <input
                        type="date"
                        value={formData.event_date}
                        onChange={(e) => setFormData((prev) => ({ ...prev, event_date: e.target.value }))}
                        className="w-full px-3 py-2 border border-[#E6DFD5] text-sm focus:outline-none focus:border-[#C25E27]"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-[#241F1C] uppercase tracking-wider mb-1">
                        By / adresse
                      </label>
                      <input
                        type="text"
                        placeholder="F.eks. 7260 Sønder Omme"
                        value={formData.event_address}
                        onChange={(e) => setFormData((prev) => ({ ...prev, event_address: e.target.value }))}
                        className="w-full px-3 py-2 border border-[#E6DFD5] text-sm focus:outline-none focus:border-[#C25E27]"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-[#241F1C] uppercase tracking-wider mb-1">
                      Eventuelle bemærkninger eller ønsker
                    </label>
                    <textarea
                      rows={3}
                      placeholder="Skriv f.eks. tidsrum, om det skal kombineres med helstegt pattegris eller catering, specielle ønsker osv."
                      value={formData.message}
                      onChange={(e) => setFormData((prev) => ({ ...prev, message: e.target.value }))}
                      className="w-full px-3 py-2 border border-[#E6DFD5] text-sm focus:outline-none focus:border-[#C25E27]"
                    />
                  </div>

                  <div className="space-y-2.5">
                    <button
                      type="submit"
                      disabled={formStatus.submitting}
                      className="w-full inline-flex items-center justify-center gap-2 bg-[#C25E27] hover:bg-[#A84E1C] disabled:bg-gray-400 text-white py-3.5 font-bold text-sm transition-colors shadow-sm cursor-pointer"
                    >
                      {formStatus.submitting ? (
                        <>
                          <i className="fa-solid fa-spinner fa-spin text-sm" aria-hidden="true" />
                          <span>Sender forespørgsel...</span>
                        </>
                      ) : (
                        <>
                          <i className="fa-solid fa-paper-plane text-xs" aria-hidden="true" />
                          <span>Send uforpligtende forespørgsel</span>
                        </>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={handleAddEquipmentToCart}
                      className="w-full inline-flex items-center justify-center gap-2 bg-[#FAF8F5] hover:bg-[#F2ECE1] text-[#241F1C] border border-[#D5C9B8] py-3.5 font-bold text-sm transition-colors shadow-xs cursor-pointer"
                    >
                      <i className="fa-solid fa-basket-shopping text-[#C25E27] text-sm" aria-hidden="true" />
                      <span>Tilføj til festkurv</span>
                    </button>
                  </div>

                  <p className="text-[11px] text-[#8C827A] text-center">
                    Vi svarer hurtigst muligt. Du kan også altid ringe direkte til os på{' '}
                    <a href="tel:25752724" className="underline font-semibold hover:text-[#241F1C]">
                      25 75 27 24
                    </a>
                    .
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Practical Information Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="border border-[#E6DFD5] p-8 sm:p-12 bg-white">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="space-y-3">
              <div className="w-10 h-10 bg-[#C25E27]/10 text-[#C25E27] flex items-center justify-center font-bold text-lg">
                <i className="fa-solid fa-location-dot" aria-hidden="true" />
              </div>
              <h3 className="font-extrabold text-lg text-[#241F1C]">Afhentning &amp; Adresse</h3>
              <p className="text-xs sm:text-sm text-[#6E635C] leading-relaxed">
                Udstyret afhentes og afleveres på <strong>Simmelbrovej 52, 7260 Sønder Omme</strong> efter forudgående
                aftale. Vi gennemgår altid udstyret og giver grundig instruktion.
              </p>
            </div>

            <div className="space-y-3">
              <div className="w-10 h-10 bg-[#C25E27]/10 text-[#C25E27] flex items-center justify-center font-bold text-lg">
                <i className="fa-solid fa-truck-fast" aria-hidden="true" />
              </div>
              <h3 className="font-extrabold text-lg text-[#241F1C]">Levering &amp; Opsætning</h3>
              <p className="text-xs sm:text-sm text-[#6E635C] leading-relaxed">
                Vil du slippe for transporten? Vi leverer, opsætter og tester det hele for dig til de faste, lave
                tillægspriser, så I blot skal tænde og nyde festen.
              </p>
            </div>

            <div className="space-y-3">
              <div className="w-10 h-10 bg-[#C25E27]/10 text-[#C25E27] flex items-center justify-center font-bold text-lg">
                <i className="fa-solid fa-utensils" aria-hidden="true" />
              </div>
              <h3 className="font-extrabold text-lg text-[#241F1C]">Kombinér med Pattegris</h3>
              <p className="text-xs sm:text-sm text-[#6E635C] leading-relaxed">
                Bestil helstegt pattegris eller grillcatering sammen med udlejningen, så sørger vi for hele
                pakken til dit arrangement fra start til slut!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to action bar */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="bg-[#241F1C] text-white p-8 sm:p-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-2 text-center md:text-left">
            <h2 className="text-xl sm:text-2xl font-bold">Har du spørgsmål til udlejningen?</h2>
            <p className="text-xs sm:text-sm text-[#DDD5C9]">
              Ring direkte til os eller send en besked – vi hjælper dig gerne med at sammensætte den perfekte fest.
            </p>
          </div>
          <div className="flex flex-wrap gap-4">
            <a
              href="tel:25752724"
              className="inline-flex items-center gap-2 bg-[#C25E27] hover:bg-[#A84E1C] text-white px-6 py-3 font-bold text-xs sm:text-sm transition-colors"
            >
              <i className="fa-solid fa-phone text-xs" aria-hidden="true" />
              <span>Ring 25 75 27 24</span>
            </a>
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 border border-white/40 hover:bg-white hover:text-[#241F1C] text-white px-6 py-3 font-bold text-xs sm:text-sm transition-colors"
            >
              <span>Kontaktformular</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
